// File: frontend/src/components/notifications/NotificationBell.tsx
// src/components/notifications/NotificationBell.tsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NotificationDto } from "../../models/NotificationModel";
import {
  getNotifications,
  getNotificationUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../services/notificationService";
import { onNotificationEvent } from "../../services/websocketService";
import { useAuth } from "../../context/AuthContext";
import { generateProfileUrl } from "../../utils/slugGenerator";
import { entryPermalinkPath } from "../../utils/canonicalUrl";
import { WelcomeRuleModal } from "./WelcomeRuleModal";
import "./NotificationBell.css";
import { parseServerDate } from "../../utils/dateUtils";

// Initial fetch size and increment per "load more" scroll
const DROPDOWN_LIMIT = 10;

// Scroll proximity threshold (px from bottom) that triggers loading next page
const SCROLL_TRIGGER_PX = 60;

const BellIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
  </svg>
);

const typeConfig: Record<string, { icon: string; className: string }> = {
  VOTE_UP:                { icon: "👍", className: "vote-up" },
  VOTE_FAV:               { icon: "⭐", className: "vote-fav" },
  ENTRY_DELETED:          { icon: "🗑️", className: "entry-deleted" },
  ACCOUNT_STATUS_CHANGED: { icon: "⚠️", className: "account-status" },
  ROLE_CHANGED:           { icon: "🎖️", className: "role-changed" },
  NEW_FOLLOWER:           { icon: "👤", className: "new-follower" },
  TOPIC_SUGGESTION_APPROVED: { icon: "✅", className: "topic-approved" },
  TOPIC_SUGGESTION_REJECTED: { icon: "❌", className: "topic-rejected" },
  ENTRY_MOVED:            { icon: "🔀", className: "entry-moved" },
  WELCOME:                { icon: "👋", className: "welcome" },
};

const formatRelativeTime = (iso: string): string => {
  // Append "Z" so the browser treats the server timestamp as UTC
  const diffMs = Date.now() - parseServerDate(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)  return "az önce";
  if (diffMin < 60) return `${diffMin} dk önce`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH} sa önce`;
  return `${Math.floor(diffH / 24)} gün önce`;
};

export const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Logged-in user — used to navigate to the user's own profile
  // (rejected suggestions are listed in the profile's owner-only tab)
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  // Inline top for the mobile fixed-position dropdown (null on desktop)
  const [mobileTop, setMobileTop] = useState<number | null>(null);
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination state for infinite scroll in the dropdown
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Required entry count for the WELCOME modal (see WelcomeRuleModal), or
  // null when the modal is closed. Read from the clicked notification's
  // targetLabel, e.g. "10".
  const [welcomeModalRequiredEntries, setWelcomeModalRequiredEntries] =
    useState<number | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Load initial unread count on mount
  useEffect(() => {
    getNotificationUnreadCount().then(setUnread).catch(() => {});
  }, []);

  // Real-time push: prepend new notification and update badge
  useEffect(() => {
    const unsubscribe = onNotificationEvent((event) => {
      setUnread(event.totalUnread);
      const fresh: NotificationDto = {
        id: event.id,
        type: event.type,
        actorId: event.actorId,
        actorUsername: event.actorUsername,
        targetId: event.targetId,
        targetLabel: event.targetLabel,
        read: false,
        createdAt: event.createdAt,
      };
      // Prepend to list but do not exceed what is already loaded
      setNotifications((prev) => [fresh, ...prev]);
    });
    return unsubscribe;
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch first page of notifications when the panel opens
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setPage(0);
    try {
      const result = await getNotifications(0, DROPDOWN_LIMIT);
      setNotifications(result.content);
      setHasMore(result.currentPage + 1 < result.totalPages);
    } catch {
      // Silently ignore — panel stays empty on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Append the next page of notifications (called on scroll-to-bottom)
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const result = await getNotifications(nextPage, DROPDOWN_LIMIT);
      setNotifications((prev) => [...prev, ...result.content]);
      setPage(nextPage);
      setHasMore(nextPage + 1 < result.totalPages);
    } catch {
      // Silently ignore
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page]);

  // Trigger loadMore when the user scrolls near the bottom of the list
  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_TRIGGER_PX;
      if (nearBottom) loadMore();
    },
    [loadMore]
  );

  const handleBellClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !open;
    // Mobile (<=600px): the dropdown is position:fixed, so anchor it just
    // below the bell button — same technique as the online users panel —
    // instead of a hard-coded CSS top that covers the header buttons.
    if (next && window.innerWidth <= 600) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setMobileTop(Math.round(rect.bottom + 8));
    } else {
      setMobileTop(null);
    }
    setOpen(next);
    if (next) loadNotifications();
  };

  const handleMarkAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await markAllNotificationsAsRead();
      setUnread(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  // Navigate to the page that contains this entry, scrolled to the entry anchor.
  //
  // This used to fetch the entry and branch on which topic type it belonged to,
  // which covered only matches and people — a notification about a club, team
  // or gündem entry dropped the reader on the home page. It also had no way to
  // know the page number, so anything past page 1 opened with no anchor to
  // scroll to. /entry/{id} answers both questions for all five domains.
  const navigateToEntry = (entryId: number) => {
    navigate(entryPermalinkPath(entryId));
  };

  const handleItemClick = async (notif: NotificationDto) => {
    if (!notif.read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
      );
      setUnread((prev) => Math.max(0, prev - 1));
      markNotificationAsRead(notif.id).catch(() => {});
    }
    setOpen(false);

    switch (notif.type) {
      case "VOTE_UP":
      case "VOTE_FAV":
      case "ENTRY_DELETED":
        // Navigate to the entry if we have its id, otherwise fall back to home
        if (notif.targetId) {
          navigateToEntry(notif.targetId);
        } else {
          navigate("/");
        }
        break;
      case "ACCOUNT_STATUS_CHANGED":
        navigate("/ayarlar");
        break;
      case "NEW_FOLLOWER":
        // targetId holds the follower's userId; targetLabel holds their username
        if (notif.actorId && notif.actorUsername) {
          navigate(generateProfileUrl(notif.actorUsername, notif.actorId));
        }
        break;
      case "TOPIC_SUGGESTION_APPROVED": {
        // targetLabel = "publishedTitle||slug" — navigate to the published topic page
        const slug = notif.targetLabel?.split("||")[1];
        if (slug) {
          navigate(`/gundem/${slug}`);
        } else {
          navigate("/");
        }
        break;
      }
      case "TOPIC_SUGGESTION_REJECTED":
        // Rejected suggestions (with admin notes) live in the owner-only tab
        // of the user's own profile page
        if (user) {
          navigate(generateProfileUrl(user.username, user.id));
        }
        break;
      case "ENTRY_MOVED": {
        // targetLabel = "entrySnippet||targetTitle||targetPath" — navigate to
        // the topic the entry was moved into
        const path = notif.targetLabel?.split("||")[2];
        navigate(path || "/");
        break;
      }
      case "WELCOME": {
        // Opens WelcomeRuleModal instead of navigating — targetLabel carries
        // the required entry count for the rookie approval ranking rule
        const required = parseInt(notif.targetLabel ?? "", 10);
        setWelcomeModalRequiredEntries(Number.isFinite(required) ? required : 0);
        break;
      }
      default:
        break;
    }
  };

  // Clicking the actor name navigates to their profile without bubbling to the row handler
  const handleActorClick = (
    e: React.MouseEvent,
    actorUsername: string,
    actorId: number
  ) => {
    e.stopPropagation();
    setOpen(false);
    navigate(generateProfileUrl(actorUsername, actorId));
  };

  // Translate a raw role/status token (e.g. "AUTHOR") coming from the backend.
  // Supports "OLD||NEW" payloads: each part is translated and joined with an
  // arrow so the user sees which role they changed FROM and TO, in their language.
  const translateRoleLabel = (raw: string | null | undefined): string => {
    if (!raw) return "";
    return raw
      .split("||")
      .map(part => t(`user_role_${part.trim().toLowerCase()}`, part))
      .join(" → ");
  };

  const translateStatusLabel = (raw: string | null | undefined): string => {
    if (!raw) return "";
    return raw
      .split("||")
      .map(part => t(`status_${part.trim().toLowerCase()}`, part))
      .join(" → ");
  };

  const buildText = (n: NotificationDto): React.ReactNode => {
    // Render actor as a clickable profile link when actor info is available
    const actor =
      n.actorUsername && n.actorId ? (
        <strong
          className="notif-actor-link"
          title={n.actorUsername}
          onClick={(e) => handleActorClick(e, n.actorUsername!, n.actorId!)}
        >
          {n.actorUsername}
        </strong>
      ) : (
        <strong>{t("notif_system")}</strong>
      );

    switch (n.type) {
      case "VOTE_UP":    return <>{actor} {t("notif_voted_your_entry")}</>;
      case "VOTE_FAV":   return <>{actor} {t("notif_faved_your_entry")}</>;
      case "ENTRY_DELETED": return <>{actor} {t("notif_deleted_your_entry")}</>;
      case "ACCOUNT_STATUS_CHANGED":
        // Plain action title — the old → new pair and the suspension duration
        // are rendered on their own lines below (see buildSnippet), mirroring
        // the ROLE_CHANGED layout.
        return <>{t("notif_account_status_changed")}</>;
      case "ROLE_CHANGED":
        // The old -> new role values are shown once, in the snippet line below
        // (see buildSnippet). Keep this line to the plain title only, otherwise
        // the role change ends up printed twice in the dropdown.
        return <>{t("notif_role_changed")}</>;
      case "NEW_FOLLOWER":
        // "actorUsername seni takip etmeye başladı"
        return <>{actor} {t("notif_new_follower")}</>;
      case "TOPIC_SUGGESTION_APPROVED":
        // "actorUsername başlık önerini onayladı"
        return <>{actor} {t("notif_topic_suggestion_approved")}</>;
      case "TOPIC_SUGGESTION_REJECTED":
        // "actorUsername başlık önerini reddetti"
        return <>{actor} {t("notif_topic_suggestion_rejected")}</>;
      case "ENTRY_MOVED":
        // Deliberately NO actor and NO inline value: moderation moves must not
        // expose which admin acted. Target topic + entry snippet are rendered
        // on their own lines below (see buildSnippet).
        return <>{t("notif_entry_moved")}</>;
      case "WELCOME":
        // System greeting — no actor line; the rule text renders as the snippet
        return <>{t("notif_welcome_title")}</>;
      default: return <>{t("notif_generic")}</>;
    }
  };

  // Snippet text shown under the notification line.
  // TOPIC_SUGGESTION_APPROVED stores "publishedTitle||slug" — only the title
  // part is displayed; the slug is used for navigation only.
  const buildSnippet = (n: NotificationDto): string | null => {
    if (!n.targetLabel) return null;
    // WELCOME stores the required entry count (e.g. "10") — render the polite
    // rookie-ranking rule sentence with that live threshold interpolated
    if (n.type === "WELCOME") {
      return t("notif_welcome_body", { count: n.targetLabel });
    }
    if (n.type === "TOPIC_SUGGESTION_APPROVED") {
      return n.targetLabel.split("||")[0];
    }
    // ENTRY_MOVED stores "entrySnippet||targetTitle||targetPath".
    // Line 1: target topic ("Yeni başlık: ..."); line 2: the entry snippet.
    if (n.type === "ENTRY_MOVED") {
      const parts = n.targetLabel.split("||");
      return `${t("notif_moved_target")}: ${parts[1] ?? ""}\n${parts[0] ?? ""}`;
    }
    // TOPIC_SUGGESTION_REJECTED stores "title — adminNote" — put the rejection
    // reason on its own line ("Sebep: ..."); .notif-snippet uses pre-line
    if (n.type === "TOPIC_SUGGESTION_REJECTED") {
      const sep = n.targetLabel.indexOf(" — ");
      if (sep === -1) return n.targetLabel;
      const title = n.targetLabel.slice(0, sep);
      const reason = n.targetLabel.slice(sep + 3);
      return `${title}\n${t("notif_rejection_reason")}: ${reason}`;
    }
    // Role/status snippets carry raw enum tokens — show them translated
    if (n.type === "ROLE_CHANGED") return translateRoleLabel(n.targetLabel);
    // ACCOUNT_STATUS_CHANGED: targetLabel = "OLD||NEW" or "OLD||NEW||days"
    // (legacy rows carry a single status). Line 1: "Old → New" pair (same
    // pattern as ROLE_CHANGED); line 2 (only for suspensions): duration.
    if (n.type === "ACCOUNT_STATUS_CHANGED") {
      const parts = (n.targetLabel ?? "").split("||");
      if (parts.length < 2) return translateStatusLabel(n.targetLabel);
      let text = `${translateStatusLabel(parts[0])} → ${translateStatusLabel(parts[1])}`;
      if (parts.length >= 3 && parts[2]) {
        text += `\n${t("notif_suspension_duration")}: ${parts[2]} ${t("notif_days")}`;
      }
      return text;
    }
    return n.targetLabel;
  };

  return (
    <div className="notif-bell-wrapper" ref={wrapperRef}>
      <button
        className="notif-bell-btn"
        onClick={handleBellClick}
        title={t("notif_bell_title")}
      >
        <BellIcon />
        {unread > 0 && (
          <span className="notif-bell-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="notif-dropdown"
          style={mobileTop !== null ? { top: mobileTop } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="notif-dropdown-header">
            <span className="notif-dropdown-title">{t("notif_panel_title")}</span>
            {unread > 0 && (
              <button className="notif-mark-all-btn" onClick={handleMarkAll}>
                {t("notif_mark_all_read")}
              </button>
            )}
          </div>

          <div className="notif-list" onScroll={handleListScroll}>
            {loading ? (
              <div className="notif-empty"><span>{t("loading")}</span></div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty">
                <BellIcon />
                <span>{t("notif_empty")}</span>
              </div>
            ) : (
              <>
                {notifications.map((notif) => {
                  const cfg =
                    typeConfig[notif.type] ?? { icon: "🔔", className: "account-status" };
                  return (
                    <div
                      key={notif.id}
                      className={`notif-item${notif.read ? "" : " unread"}`}
                      onClick={() => handleItemClick(notif)}
                    >
                      <div className={`notif-icon ${cfg.className}`}>{cfg.icon}</div>

                      <div className="notif-body">
                        <p className="notif-text">{buildText(notif)}</p>
                        {/* Hide targetLabel snippet for types where it is not a content excerpt */}
                        {notif.targetLabel &&
                          notif.type !== "NEW_FOLLOWER" &&
                          buildSnippet(notif) && (
                            <span className="notif-snippet">
                              {/* Every piece of information gets its own line —
                                  buildSnippet separates them with \n */}
                              {/* Long values (rejection reasons, target topics,
                                  entry snippets) are cut with ellipsis; the full
                                  text is available via the native title tooltip */}
                              {buildSnippet(notif)!.split("\n").map((line, i) => (
                                <div key={i} className="notif-snippet-line" title={line}>
                                  {line}
                                </div>
                              ))}
                            </span>
                          )}
                        <span className="notif-time">{formatRelativeTime(notif.createdAt)}</span>
                      </div>

                      {!notif.read && <div className="notif-unread-dot" />}
                    </div>
                  );
                })}

                {/* Load-more indicator shown while fetching the next page */}
                {loadingMore && (
                  <div className="notif-load-more-spinner">
                    <span>{t("loading")}</span>
                  </div>
                )}

                {/* End-of-list message when all pages are exhausted */}
                {!hasMore && notifications.length >= DROPDOWN_LIMIT && (
                  <div className="notif-end-of-list">
                    <span>{t("notif_all_loaded")}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {welcomeModalRequiredEntries !== null && (
        <WelcomeRuleModal
          requiredEntries={welcomeModalRequiredEntries}
          onClose={() => setWelcomeModalRequiredEntries(null)}
        />
      )}
    </div>
  );
};